<template>
  <div ref="columnRef" :class="columnClasses">
    <component is="style" v-if="model.state.customCss" type="text/css">
      {{ model.state.customCss.state?.code }}
    </component>
    <component is="style" v-if="columnCssStyle" type="text/css">
      {{ columnCssStyle }}
    </component>

    <component is="style" v-if="columnTagStyle" type="text/css">
      {{ columnTagStyle }}
    </component>
    <div
      :id="columnId"
      v-click-away="removeActive"
      class="grid__col-container"
      :style="columnStyling"
      :class="columnContainerClasses"
      @mouseover.stop="setFocus"
      @mouseout.stop="removeFocus"
      @click.right="showContextMenu($event, model, model.state.edit?.isActive)"
      @click="toggleActive($event)"
    >
      <div
        v-if="campaignState.isEditModeActive && sectionType === globalState.editing?.activeTabCategory"
        class="edit-title"
      >
        {{ model.state.label }}
      </div>

      <DeleteElement
        v-if="
          model.state.edit?.isActive && model.getSection().getSectionType() === globalState.editing?.activeTabCategory
        "
        :model="model"
      />

      <div
        v-if="isBackgroundEnabled"
        class="grid__col-container-bg-color"
        :style="model.state.settings?.state.elementStyling?.background?.color"
      ></div>

      <div
        v-if="model.state.settings?.state.config.isBackgroundImageEnabled"
        class="grid__col-container-bg"
        :style="backgroundImageStyle"
      ></div>

      <div
        v-if="isColumnOverlayEnabled"
        class="grid__col-container-bg-overlay"
        :style="model.state.settings?.state?.elementStyling?.background?.overlay"
      ></div>

      <div
        v-if="isColumnGradientEnabled"
        class="grid__col-container-bg-overlay"
        :style="model.state.settings?.state?.elementStyling?.background?.overlay"
      ></div>

      <div class="grid__col-addons">
        <AddonWrap v-for="addon in model.state.addons" :key="addon.state.modelId" :addon="addon" />
      </div>
    </div>

    <LFNavigatorContentAdd
      v-if="
        (campaignState.isEditModeActive && model.state.edit?.isActive) ||
        (campaignState.isEditModeActive &&
          isColumnEmpty() &&
          model.getSection().getSectionType() === globalState.editing?.activeTabCategory) ||
        (campaignState.isEditModeActive && model.state.edit?.hasActiveAddon) ||
        (campaignState.isEditModeActive && hasModalOpen)
      "
      :column-model="model"
      @modalOpen="hasModalOpen = true"
      @modalClose="hasModalOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { computed, CSSProperties, defineComponent, onMounted, PropType, ref, watch } from 'vue';
import ColumnModel from '@/components/layout/column/ColumnModel';
import AddonWrap from '@/components/addons/AddonWrap.vue';
import useNavigator from '@/hooks/useNavigator';
import DeleteElement from '@/components/components/editing/DeleteElement.vue';
import LFNavigatorContentAdd from '@/components/components/editing/Navigator/LFNavigatorContentAdd.vue';
import { BackgroundType, OverlayType } from '@/typings/enums/enums';
import { useStore } from '@/store';
import { ClassList } from '@/typings/types/types';
import { applyReplacementTags, isSsr } from '@/utilities/Utilities';
import { warmAudio } from '@/utilities/AudioPlayer';
import useLayoutSettings from '@/components/layout/hooks/useInlineLayoutStyles';

export default defineComponent({
  name: 'Column',
  components: {
    LFNavigatorContentAdd,
    DeleteElement,
    AddonWrap
  },
  props: {
    model: {
      type: Object as PropType<ColumnModel>,
      required: true
    }
  },
  setup(props) {
    const columnRef = ref<HTMLElement | null>(null);
    const sectionType = props.model.getSection().getSectionType();
    const shouldAnimateIn = ref(
      props.model.state.settings?.state.advanced?.transitions?.enter &&
        props.model.state.settings?.state.advanced.transitions.enter !== 'none'
    );
    const animateIn = ref(false);
    const hasAnimatedIn = ref(false);
    const hasModalOpen = ref(false);
    const { setFocus, removeFocus } = useNavigator(props.model);
    const store = useStore();
    const campaignState = store.getters['campaign/state'];

    const columnCssStyle = computed(() => {
      let css = '';

      if (
        !props.model.state.settings.state.style.content?.useDefaultTextColor &&
        props.model.state.settings.state.style?.content?.color &&
        typeof props.model.state.settings.state.style?.content?.color !== 'undefined'
      ) {
        css = `
          .section .grid__row .${props.model.state.classIdentifier} .lf-text,
          .section .grid__row .${props.model.state.classIdentifier} p,
          .section .grid__row .${props.model.state.classIdentifier} a,
          .section .grid__row .${props.model.state.classIdentifier} h1,
          .section .grid__row .${props.model.state.classIdentifier} h2,
          .section .grid__row .${props.model.state.classIdentifier} h3,
          .section .grid__row .${props.model.state.classIdentifier} h4,
          .section .grid__row .${props.model.state.classIdentifier} h5,
          .section .grid__row .${props.model.state.classIdentifier} h6 {
            color: ${props.model.state.settings.state.elementStyling.background?.general?.color} !important;
          }
        `;
      }

      return css;
    });

    const columnClick = (event?: Event) => {
      if (event && props.model.state.action?.state.type) {
        warmAudio();
        event.stopPropagation();
      }

      props.model.state.action?.triggerAction(props.model);
    };

    const toggleActive = (event: Event) => {
      if (campaignState.isEditModeActive && sectionType === store.state.editing?.activeTabCategory) {
        warmAudio();
        event.stopPropagation();

        store.dispatch('editing/setActiveModel', {
          model: props.model
        });
      } else {
        columnClick(event);
      }
    };

    const showContextMenu = (event: MouseEvent, model: ColumnModel, isActive: boolean | undefined) => {
      if (campaignState.isEditModeActive) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof isActive === 'boolean') {
          store.dispatch('editing/showContextMenu', {
            model,
            event,
            isActive
          });
        }
      }
    };

    const removeActive = () => {
      if (
        !hasModalOpen.value &&
        campaignState.isEditModeActive &&
        (props.model as ColumnModel).getSection().getSectionType() === store.state.editing?.activeTabCategory
      ) {
        store.dispatch('editing/removeActiveModel');
      }
    };

    const isColumnEmpty = () => {
      return props.model.state.addons.length === 0;
    };

    const isBackgroundEnabled = computed(() => {
      return props.model.state.settings?.state.style?.background?.backgroundType !== BackgroundType.TRANSPARENT;
    });

    const columnTagStyle = computed(() => {
      // check for overwriting default color
      if (
        !props.model.state.settings.state.style.content?.useDefaultTextColor &&
        props.model.state.settings.state.style.content?.color &&
        props.model.state.settings.state.style.content?.color !== ''
      ) {
        const classIdentifier = `.${props.model.state.classIdentifier}`;
        return `.section .grid__row ${classIdentifier} .lf-text,
                .section .grid__row ${classIdentifier} p,
                .section .grid__row ${classIdentifier} a,
                .section .grid__row ${classIdentifier} h1,
                .section .grid__row ${classIdentifier} h2,
                .section .grid__row ${classIdentifier} h3,
                .section .grid__row ${classIdentifier} h4,
                .section .grid__row ${classIdentifier} h5,
                .section .grid__row ${classIdentifier} h6 { color: ${props.model.state.settings.state.style.content?.color} !important; }
              `;
      }
      return undefined;
    });

    const isColumnOverlayEnabled = computed(() => {
      return (
        props.model.state.settings?.state.elementStyling?.background?.overlay &&
        props.model.state.settings?.state.style?.overlay?.overlayType === OverlayType.SOLID
      );
    });

    const isColumnGradientEnabled = computed(() => {
      return (
        props.model.state.settings?.state.elementStyling?.background?.overlay &&
        props.model.state.settings?.state.style?.overlay?.overlayType === OverlayType.GRADIENTS
      );
    });

    const columnClasses = computed(() => {
      return {
        [props.model.state.classIdentifier]: true,
        'grid__col--empty': isColumnEmpty(),
        [`grid__col col-md-${props.model.state.size}`]: !!props.model.state.size,
        'column--edit-focus': props.model.state.edit?.isFocus && !props.model.state.edit?.isActive,
        'column--active-in-navigator': props.model.state.edit?.isActive,
        'grid__col--addon-active-in-navigator':
          props.model.state.edit?.hasActiveAddon &&
          campaignState.edit?.enabled &&
          campaignState.isEditModeActive &&
          store.state.editing?.activeTabCategory === sectionType
      };
    });

    const columnContainerClasses = computed(() => {
      const animateClassList: ClassList = {};
      if (animateIn.value && campaignState.contentReady && !hasAnimatedIn.value) {
        animateClassList[`animated ${props.model.state.settings?.state.advanced?.transitions?.enter}`] = true;
      }

      return {
        [props.model.state.settings?.state.advanced?.advanced?.classname ?? '']:
          !!props.model.state.settings?.state.advanced?.advanced?.classname,
        'grid__col-container--has-click': !!props.model.state.action?.state,
        ...animateClassList
      };
    });

    const columnId = computed(() => {
      return props.model.state.settings?.state.advanced?.advanced?.elementId;
    });

    const columnStyling = computed(() => {
      const enterDuration = props.model.state.settings?.state.advanced?.transitions?.enterDuration
        ? props.model.state.settings?.state.advanced.transitions.enterDuration
        : 525;
      const enterDelay = props.model.state.settings?.state.advanced?.transitions?.enterDelay
        ? props.model.state.settings?.state.advanced.transitions.enterDelay
        : 0;

      let columnTransition = {};
      if (props.model.state.settings?.state.advanced?.transitions?.enter) {
        columnTransition = {
          animationDuration: enterDuration + 'ms',
          animationDelay: enterDelay + 'ms',
          transitionDelay: enterDelay + 'ms'
        };
      }

      const columOpacity = { opacity: 1 };

      if (shouldAnimateIn.value) {
        columOpacity.opacity = 0;
      }

      return {
        ...useLayoutSettings().applyInlineStyles(
          props.model.state?.settings?.state?.elementStyling?.background?.general
        ),
        ...columOpacity,
        ...columnTransition
      };
    });

    // Background image style is ejected out in a computed to properly handle
    // replacement of replacement tags, such as #date#.
    const backgroundImageStyle = computed<CSSProperties>(() => {
      let style: CSSProperties = {};

      if (props.model.state.settings?.state.elementStyling?.background?.image) {
        style = { ...props.model.state.settings.state.elementStyling.background?.image };

        if (style.backgroundImage) {
          style.backgroundImage = applyReplacementTags(style.backgroundImage);
        }
      }

      return style;
    });

    watch(
      () => props.model.state.edit?.isActive,
      (isActive) => {
        if (isActive && campaignState.isEditModeActive) {
          hasModalOpen.value = false;
          props.model.row.section.state.edit?.sectionRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    );

    // InterSectionObserver data
    let playedEnter = false;

    const onAnimationEnd = () => {
      columnRef.value?.removeEventListener('animationend', onAnimationEnd);
      hasAnimatedIn.value = true;
    };

    onMounted(() => {
      columnRef.value?.addEventListener('animationend', onAnimationEnd);
      props.model.state.action?.startAutoTrigger(columnClick);

      if (!isSsr() && columnRef.value) {
        // only section should have InterSectionObserver
        if (shouldAnimateIn.value) {
          // observe if section is in intersection
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry && entry.isIntersecting && !playedEnter && columnRef.value) {
                // @ts-ignore
                observer.unobserve(columnRef.value);
                playedEnter = true;

                // Play animation
                animateIn.value = true;
                shouldAnimateIn.value = false;
              }
            },
            {
              threshold: 0.3
            }
          );
          // @ts-ignore
          observer.observe(columnRef.value);
        }
      }
    });

    return {
      sectionType,
      columnRef,
      globalState: store.state,
      campaignState,
      isBackgroundEnabled,
      isColumnGradientEnabled,
      isColumnOverlayEnabled,
      isColumnEmpty,
      columnClasses,
      columnContainerClasses,
      columnId,
      columnStyling,
      backgroundImageStyle,
      columnTagStyle,
      columnClick,
      showContextMenu,
      setFocus,
      removeFocus,
      toggleActive,
      removeActive,
      hasModalOpen,
      columnCssStyle
    };
  }
});
</script>

<style lang="scss">
.site--editmode-on {
  .grid__col {
    .grid__col-container {
      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: 1px solid transparent;
        z-index: 10;
        pointer-events: none;
      }
    }

    &--addon-active-in-navigator {
      & > .grid__col-container {
        &:before {
          border-color: $edit-border-color;
        }
      }
    }

    &--empty {
      min-height: 120px;
      .grid__col-container {
        border: 1px dashed hsla(0, 0%, 80%, 0.5);
      }
      .content-item__add {
        left: 50%;
        top: 50%;
        margin-left: -12px;
        margin-top: -12px;
      }
    }
  }
  .column {
    .edit-title {
      display: none;
    }
    &--edit-focus {
      & > .grid__col-container {
        &:before {
          border-style: dashed;
          border-color: $edit-border-hover-color;
        }
        & > .edit-title {
          @include lf-title();
          background-color: rgba(#878787, 0.9);
        }
      }
    }
    &--active-in-navigator {
      & > .grid__col-container {
        &:before {
          border-color: $edit-border-color;
        }
        & > .edit-title {
          @include lf-title();
          background-color: $edit-border-color;
          color: #fff;
          z-index: 99999;
        }
      }
    }
  }
}
</style>
